<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="8">
        <!-- Navegación -->
        <div v-if="!hideActions" class="d-flex align-center mb-4">
          <v-btn icon variant="text" @click="$router.back()" class="mr-2">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>Detalles de la Pregunta</h2>

          <v-spacer />

          <!-- Acciones (solo para profesores/admins) -->
          <div v-if="canManageQuestion" class="d-flex gap-2">
            <v-btn
              color="warning"
              variant="outlined"
              prepend-icon="mdi-pencil"
              @click="editQuestion"
              :disabled="loading"
            >
              Editar
            </v-btn>
            <v-btn
              color="error"
              variant="outlined"
              prepend-icon="mdi-delete"
              @click="confirmDelete"
              :disabled="loading"
            >
              Eliminar
            </v-btn>
          </div>
        </div>

        <!-- Botón de imprimir (siempre visible cuando hay pregunta) -->
        <div v-if="question && !loading" class="d-flex justify-end mb-3">
          <v-btn
            color="info"
            variant="outlined"
            prepend-icon="mdi-printer"
            @click="printQuestion"
            size="small"
          >
            Imprimir
          </v-btn>
        </div>

        <!-- Loading -->
        <v-card v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4">Cargando pregunta...</div>
        </v-card>

        <!-- Contenido de la pregunta -->
        <div v-else-if="question" ref="printArea" class="printable-content">
          <!-- Información básica -->
          <v-card class="mb-4" elevation="2">
            <v-card-text>
              <div class="d-flex flex-wrap gap-2 mb-4">
                <v-chip
                  v-if="question.school_subject?.name"
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-book-open-variant"
                >
                  {{ question.school_subject.name }}
                </v-chip>
                <v-chip v-else color="grey" variant="outlined" prepend-icon="mdi-book-off">
                  Sin asignatura
                </v-chip>

                <v-chip color="info" variant="outlined" prepend-icon="mdi-clock">
                  {{ formatTime(question.time_limit) }}
                </v-chip>

                <v-chip
                  v-if="question.school_grade?.grade"
                  color="success"
                  variant="outlined"
                  prepend-icon="mdi-school"
                >
                  Grado {{ question.school_grade?.grade }}
                </v-chip>

                <v-chip v-else color="grey" variant="outlined" prepend-icon="mdi-school-outline">
                  Sin grado asignado
                </v-chip>
              </div>

              <!-- Autor y fecha (solo para profesores/admins) -->
              <div v-if="!isStudent" class="d-flex align-center mb-4">
                <v-avatar size="32" class="mr-3">
                  <v-img
                    v-if="question.author?.avatar"
                    :src="question.author.avatar"
                    :alt="question.author.name"
                  />
                  <v-icon v-else>mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ question.author?.name || 'N/D' }}</div>
                  <div class="text-caption text-medium-emphasis">
                    Creado: {{ formatDate(question.created_at) }}
                    <span v-if="question.updated_at !== question.created_at">
                      • Actualizado: {{ formatDate(question.updated_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Enunciado -->
              <div class="question-statement">
                <h3 class="mb-3">Pregunta:</h3>
                <div class="text-body-1 text-justify">
                  <MathRenderer :content="question.statement" />
                </div>
              </div>

              <!-- Imagen del enunciado -->
              <div v-if="question.statement_image_url" class="mt-4">
                <AuthenticatedImage
                  :src="question.statement_image_url"
                  max-height="400"
                  image-class="border rounded"
                />
              </div>
            </v-card-text>
          </v-card>

          <!-- Opciones de respuesta -->
          <v-card class="mb-4" elevation="2">
            <v-card-title>
              <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
              Opciones de Respuesta
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="(option, index) in question.options"
                  :key="option.id || index"
                  cols="12"
                  md="6"
                >
                  <v-card
                    :class="[
                      'option-card pa-3',
                      {
                        'correct-option': !isStudent && option.is_correct,
                        'border-success': !isStudent && option.is_correct,
                      },
                    ]"
                    variant="outlined"
                  >
                    <div class="d-flex align-center">
                      <v-avatar
                        size="32"
                        :color="!isStudent && option.is_correct ? 'success' : 'grey-lighten-2'"
                        class="mr-3"
                      >
                        <span class="text-h6 font-weight-bold">
                          {{ option.label }}
                        </span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <!-- Texto de la opción -->
                        <div v-if="option.text" class="option-text">
                          <MathRenderer :content="option.text" />
                        </div>
                        <!-- Imagen de la opción -->
                        <div v-if="option.image_url" class="option-image">
                          <AuthenticatedImage
                            :src="option.image_url"
                            max-height="120"
                            image-class="border rounded"
                          />
                        </div>
                        <div v-if="!option.text && !option.image_url" class="text-disabled">
                          Opción sin contenido
                        </div>
                      </div>
                      <v-icon v-if="!isStudent && option.is_correct" color="success" class="ml-2">
                        mdi-check-circle
                      </v-icon>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Justificación (solo para profesores/admins) -->
          <v-card v-if="!isStudent && (question.justification || question.justification_image_url)" class="mb-4" elevation="2">
            <v-card-title>
              <v-icon class="mr-2">mdi-lightbulb</v-icon>
              Justificación
            </v-card-title>
            <v-card-text>
              <div v-if="question.justification" class="text-body-1">
                <MathRenderer :content="question.justification" />
              </div>
              <div v-if="question.justification_image_url" class="mt-3">
                <AuthenticatedImage
                  :src="question.justification_image_url"
                  max-height="400"
                  image-class="border rounded"
                />
              </div>
            </v-card-text>
          </v-card>

          <!-- Estadísticas (solo para profesores/admins) -->
          <v-card v-if="!isStudent && question.statistics" class="mb-4" elevation="2">
            <v-card-title>
              <v-icon class="mr-2">mdi-chart-bar</v-icon>
              Estadísticas
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h4 primary--text">
                      {{ question.statistics.total_answers || 0 }}
                    </div>
                    <div class="text-caption">Total respuestas</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h4 success--text">
                      {{ question.statistics.correct_answers || 0 }}
                    </div>
                    <div class="text-caption">Correctas</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h4 error--text">
                      {{
                        (question.statistics.total_answers || 0) -
                        (question.statistics.correct_answers || 0)
                      }}
                    </div>
                    <div class="text-caption">Incorrectas</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h4 info--text">
                      {{ calculateSuccessRate(question.statistics) }}%
                    </div>
                    <div class="text-caption">Éxito</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Botón para responder (solo estudiantes) -->
          <div v-if="isStudent && !hideActions" class="text-center">
            <v-btn
              size="large"
              color="primary"
              variant="elevated"
              prepend-icon="mdi-play"
              @click="startAnswering"
            >
              Responder Pregunta
            </v-btn>
          </div>
        </div>

        <!-- Error -->
        <v-alert v-else-if="error" type="error" variant="tonal" class="mb-4">
          <v-icon>mdi-alert-circle</v-icon>
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Dialog de confirmación para eliminar -->
    <ConfirmDialog
      v-model="deleteDialog"
      title="Confirmar eliminación"
      text="¿Está seguro que desea eliminar esta pregunta? Esta acción no se puede deshacer."
      confirm-text="Eliminar"
      color="error"
      :loading="deleteLoading"
      @confirmed="handleDeleteQuestion"
      @cancelled="deleteDialog = false"
    />

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'

export default {
  name: 'QuestionDetail',

  components: {
    AuthenticatedImage,
    ConfirmDialog,
    MathRenderer,
  },

  props: {
    questionId: {
      type: [String, Number],
      required: true,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      deleteDialog: false,
      deleteLoading: false,

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    }
  },

  computed: {
    ...mapGetters('questions', ['currentQuestion', 'loading', 'error']),
    ...mapGetters('auth', ['isStudent', 'isTeacher', 'isAdmin', 'user']),

    question() {
      return this.currentQuestion
    },

    canManageQuestion() {
      if (this.isStudent) return false
      if (this.isAdmin) return true
      if (this.isTeacher && this.question?.author?.id === this.user?.id) return true
      return false
    },
  },

  async created() {
    await this.loadQuestion()
  },

  methods: {
    ...mapActions('questions', ['fetchQuestionById', 'deleteQuestion']),

    async loadQuestion() {
      const result = await this.fetchQuestionById(this.questionId)

      if (!result.success) {
        this.showSnackbar(result.error, 'error')
      }
    },

    editQuestion() {
      this.$router.push(`/questions/${this.questionId}/edit`)
    },

    confirmDelete() {
      this.deleteDialog = true
    },

    async handleDeleteQuestion() {
      this.deleteLoading = true

      try {
        const result = await this.deleteQuestion(this.questionId)

        if (result.success) {
          this.deleteDialog = false
          this.showSnackbar('Pregunta eliminada exitosamente', 'success')
          setTimeout(() => {
            this.$router.push('/questions')
          }, 1500)
        } else {
          this.showSnackbar(result.error, 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al eliminar la pregunta', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    startAnswering() {
      this.$router.push(`/questions/${this.questionId}/answer`)
    },

    printQuestion() {
      const printContent = this.$refs.printArea?.innerHTML
      if (!printContent) {
        this.showSnackbar('No hay contenido para imprimir', 'error')
        return
      }

      // Abrimos una nueva ventana
      const printWindow = window.open('', '', 'width=900,height=700')

      // Copiamos todos los estilos CSS del documento principal (incluyendo los de Vuetify)
      let styles = ''
      document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
        styles += node.outerHTML
      })

      // Escribimos el HTML dentro del popup
      printWindow.document.write(`
        <html>
          <head>
            <title>Impresión de Pregunta</title>
            ${styles}
            <style>
              /* Ajustes específicos para impresión */
              body {
                margin: 20px;
                background: white;
                color: #000;
              }

              .v-container,
              .v-card {
                box-shadow: none !important;
              }

              .v-card {
                border: 1px solid #ddd !important;
                margin-bottom: 16px;
                page-break-inside: avoid;
              }

              .correct-option {
                border: 2px solid #4caf50 !important;
                background-color: #f1f8f4 !important;
              }

              img, .v-img {
                max-width: 100%;
                height: auto;
              }

              /* Oculta elementos que no son relevantes en impresión */
              .v-btn,
              .v-icon.mdi-arrow-left,
              nav,
              aside {
                display: none !important;
              }

              /* Evita que los textos se corten entre páginas */
              .v-card, .option-card {
                page-break-inside: avoid;
              }

              @page {
                margin: 2cm;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `)

      printWindow.document.close()
      printWindow.focus()

      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 600)
    },

    getDifficultyColor(difficulty) {
      const colors = {
        easy: 'success',
        medium: 'warning',
        hard: 'error',
      }
      return colors[difficulty] || 'grey'
    },

    getDifficultyLabel(difficulty) {
      const labels = {
        easy: 'Fácil',
        medium: 'Medio',
        hard: 'Difícil',
      }
      return labels[difficulty] || difficulty
    },

    formatTime(seconds) {
      if (!seconds) return 'N/D'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    formatDate(dateString) {
      if (!dateString) return 'N/D'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    calculateSuccessRate(statistics) {
      if (!statistics || !statistics.total_answers) return 0
      return Math.round((statistics.correct_answers / statistics.total_answers) * 100)
    },

    showSnackbar(message, color = 'success', timeout = 4000) {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout,
      }
    },
  },
}
</script>

<style scoped>
.question-statement {
  background: rgba(var(--v-theme-primary), 0.05);
  border-radius: 8px;
  padding: 16px;
}

.option-card {
  transition: all 0.2s ease;
}

.option-card:hover {
  transform: translateY(-1px);
}

.correct-option {
  background: rgba(var(--v-theme-success), 0.05);
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rounded {
  border-radius: 4px;
}

.border-success {
  border-color: rgb(var(--v-theme-success)) !important;
  border-width: 2px !important;
}

.d-flex.gap-2 > * {
  margin-right: 8px;
}
</style>
